import React, { useState } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography,
} from "@mui/material";
import { format } from "date-fns";

import API from "../../utils/Axios.utils";
import logger from "../../utils/logger";

// Same limit as the backend (offerController REJECTION_REASON_MAX_LENGTH).
export const REJECTION_REASON_MAX_LENGTH = 2000;

const Field = ({ label, children }) => (
  <Grid item xs={6}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" component="div">{children || "—"}</Typography>
  </Grid>
);

/**
 * Manager's review of one offer submitted for approval: shows the candidate,
 * job and the generated letter, then approves it (the recruiter can send it)
 * or rejects it with a reason (back to the recruiter as a draft).
 *
 * The letter is HTML written by the recruiter, so it is shown in a sandboxed
 * frame where it cannot run scripts.
 */
const OfferReviewDialog = ({ offer, onClose, onDecided, notify }) => {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const candidate = offer.profile?.candidate_name || "Candidate";
  const trimmedReason = reason.trim();

  const decide = async (action) => {
    const isApprove = action === "approve";
    setSaving(true);
    logger.info(`[Approvals] ${isApprove ? "Approving" : "Rejecting"} offer ${offer.offer_id} (${candidate}).`);
    try {
      if (isApprove) {
        await API.post("/api/offers/bulk-approve", { offer_ids: [offer.offer_id] });
      } else {
        await API.post("/api/offers/bulk-reject", { offer_ids: [offer.offer_id], reason: trimmedReason });
      }
      notify(isApprove ? `Offer for ${candidate} approved.` : `Offer for ${candidate} sent back to the recruiter.`, "success");
      onDecided();
      onClose();
    } catch (err) {
      const message = err?.response?.data?.error || "Could not save your decision. Please try again.";
      logger.error(`[Approvals] ${action} of offer ${offer.offer_id} failed — ${message}`);
      notify(message, "error");
      // 409: another manager already decided — refresh so it leaves the list.
      if (err?.response?.status === 409) {
        onDecided();
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={(e, why) => { if (why !== "backdropClick" && !saving) onClose(); }} maxWidth="md" fullWidth>
      <DialogTitle>Review offer — {candidate}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Field label="Candidate">{candidate}{offer.profile?.email ? ` (${offer.profile.email})` : ""}</Field>
          <Field label="Job">{[offer.job?.job_code, offer.job?.title].filter(Boolean).join(" — ")}</Field>
          <Field label="Compensation">{offer.compensation}</Field>
          <Field label="Template">{offer.template?.template_name}</Field>
          <Field label="Submitted by">{offer.submitted_by_name}</Field>
          <Field label="Submitted on">{offer.submitted_at ? format(new Date(offer.submitted_at), "dd MMM yyyy, HH:mm") : null}</Field>
        </Grid>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Offer letter</Typography>
        <Box
          component="iframe"
          title={`Offer letter for ${candidate}`}
          sandbox=""
          srcDoc={offer.offer_text || ""}
          sx={{ width: "100%", height: 420, border: 1, borderColor: "divider", borderRadius: 1, bgcolor: "#fff" }}
        />

        {rejecting && (
          <TextField
            label="Reason for rejection"
            helperText={`The recruiter sees this reason. ${trimmedReason.length}/${REJECTION_REASON_MAX_LENGTH}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            inputProps={{ maxLength: REJECTION_REASON_MAX_LENGTH }}
            multiline
            minRows={2}
            fullWidth
            autoFocus
            required
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        {rejecting ? (
          <>
            <Button onClick={() => setRejecting(false)} disabled={saving}>Back</Button>
            <Button color="error" variant="contained" disabled={saving || !trimmedReason} onClick={() => decide("reject")}>
              Confirm rejection
            </Button>
          </>
        ) : (
          <>
            <Button color="error" variant="outlined" disabled={saving} onClick={() => setRejecting(true)}>
              Reject
            </Button>
            <Button variant="contained" disabled={saving} onClick={() => decide("approve")}>
              Approve
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OfferReviewDialog;
